import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { storage } from '../firebase/config';

/**
 * Upload an image file to Firebase Storage
 * @param {File} file - The image file to upload
 * @param {string} folder - The folder path in storage (e.g., 'events/backgrounds')
 * @param {string} fileName - Optional custom filename. If not provided, will use file name with timestamp
 * @returns {Promise<string>} - Returns the download URL of the uploaded image
 */
export const uploadImage = async (file, folder = 'images', fileName = null) => {
  try {
    // Validate file type
    if (!file.type.startsWith('image/')) {
      throw new Error('File must be an image');
    }

    // Validate file size (800KB = 800 * 1024 bytes)
    // const maxSizeBytes = 800 * 1024;
    // if (file.size > maxSizeBytes) {
    //   throw new Error(`File size must be less than 800KB. Current size: ${Math.round(file.size / 1024)}KB`);
    // }

    // Generate unique filename if not provided
    const timestamp = Date.now();
    const sanitizedFileName = fileName || `${timestamp}_${file.name.replace(/[^a-zA-Z0-9.-]/g, '_')}`;
    
    // Create storage reference
    const storageRef = ref(storage, `${folder}/${sanitizedFileName}`);
    
    // Upload the file
    const snapshot = await uploadBytes(storageRef, file);
    
    // Get the download URL
    const downloadURL = await getDownloadURL(snapshot.ref);
    
    console.log('Image uploaded successfully:', downloadURL);
    return downloadURL;
  } catch (error) {
    console.error('Error uploading image:', error);
    throw new Error(`Failed to upload image: ${error.message}`);
  }
};

/**
 * Delete an image from Firebase Storage using its URL
 * @param {string} imageUrl - The download URL of the image to delete
 * @returns {Promise<void>}
 */
export const deleteImage = async (imageUrl) => {
  try {
    if (!imageUrl || !imageUrl.includes('firebase')) {
      console.warn('Invalid Firebase Storage URL provided for deletion');
      return;
    }

    // Extract the storage path from the URL
    const decodedUrl = decodeURIComponent(imageUrl);
    const pathMatch = decodedUrl.match(/\/o\/(.+?)\?/);
    
    if (!pathMatch) {
      throw new Error('Could not extract storage path from URL');
    }

    const storagePath = pathMatch[1];
    const storageRef = ref(storage, storagePath);
    
    await deleteObject(storageRef);
    console.log('Image deleted successfully:', imageUrl);
  } catch (error) {
    console.error('Error deleting image:', error);
    // Don't throw error for deletion failures to avoid breaking the app
  }
};

/**
 * Helper function to extract filename from storage URL for display purposes
 * @param {string} url - Firebase Storage download URL
 * @returns {string} - Original filename or 'uploaded-image' as fallback
 */
export const getFileNameFromUrl = (url) => {
  try {
    if (!url) return 'uploaded-image';
    
    const decodedUrl = decodeURIComponent(url);
    const pathMatch = decodedUrl.match(/\/o\/(.+?)\?/);
    
    if (pathMatch) {
      const fullPath = pathMatch[1];
      const fileName = fullPath.split('/').pop();
      // Remove timestamp prefix if present
      return fileName.replace(/^\d+_/, '');
    }
    
    return 'uploaded-image';
  } catch (error) {
    console.error('Error extracting filename from URL:', error);
    return 'uploaded-image';
  }
};

/**
 * Check if a URL is a Firebase Storage URL
 * @param {string} url - URL to check
 * @returns {boolean}
 */
export const isFirebaseStorageUrl = (url) => {
  return url && typeof url === 'string' && url.includes('firebasestorage.googleapis.com');
};

/**
 * Check if a URL is a base64 data URL
 * @param {string} url - URL to check
 * @returns {boolean}
 */
export const isBase64DataUrl = (url) => {
  return url && typeof url === 'string' && url.startsWith('data:image/');
};

/**
 * Validate image file before upload
 * @param {File} file - The file to validate
 * @returns {Object} - Validation result with success boolean and error message if any
 */
export const validateImageFile = (file) => {
  if (!file) {
    return { success: false, error: 'No file selected' };
  }

  // Check file type
  if (!file.type.startsWith('image/')) {
    return { success: false, error: 'File must be an image (PNG, JPEG, etc.)' };
  }

  // Check file size (800KB = 800 * 1024 bytes)
  // const maxSizeBytes = 800 * 1024;
  // if (file.size > maxSizeBytes) {
  //   const fileSizeKB = Math.round(file.size / 1024);
  //   return { 
  //     success: false, 
  //     error: `File size must be less than 800KB. Current size: ${fileSizeKB}KB` 
  //   };
  // }

  return { success: true };
};
